export const HOLLYWOOD_QUALIFICATION_QUESTION_ID = "qualification";

export const HOLLYWOOD_QUALIFICATION_CALENDLY_POSITION = 4;

export const HOLLYWOOD_QUALIFICATION_CALENDLY_QUESTION =
  "Jakiego poziomu wsparcia potrzebujesz, aby osiągnąć swoją metamorfozę?";

export const HOLLYWOOD_QUALIFICATION_OPTION_IDS = {
  fullMetamorphosis: "qual-1",
  simplePlan: "qual-2",
  investmentReady: "qual-1",
  investmentConsidering: "qual-2",
  investmentBelow200: "qual-3",
} as const;

export const HOLLYWOOD_QUALIFICATION_OPTION_VALUES = {
  fullMetamorphosis: "full_metamorphosis",
  simplePlan: "simple_plan",
  investmentReady: "investment_500_700_ready",
  investmentConsidering: "investment_up_to_500_considering",
  investmentBelow200: "investment_below_200_cheaper",
} as const;

export type HollywoodQualificationOption = {
  id: string;
  label: string;
  value: string;
};

export const HOLLYWOOD_QUALIFICATION_OPTIONS = [
  {
    id: HOLLYWOOD_QUALIFICATION_OPTION_IDS.fullMetamorphosis,
    label: "Chcę schudnąć raz na zawsze (metamorfoza ze wsparciem trenera)",
    value: HOLLYWOOD_QUALIFICATION_OPTION_VALUES.fullMetamorphosis,
  },
  {
    id: HOLLYWOOD_QUALIFICATION_OPTION_IDS.simplePlan,
    label: "Wolę działać sama według gotowego planu, bez regularnych konsultacji",
    value: HOLLYWOOD_QUALIFICATION_OPTION_VALUES.simplePlan,
  },
] satisfies HollywoodQualificationOption[];

export const HOLLYWOOD_SPRING_QUALIFICATION_OPTIONS = [
  {
    id: HOLLYWOOD_QUALIFICATION_OPTION_IDS.investmentReady,
    label: "500 - 700 zł miesięcznie - jestem gotowa rozpocząć współpracę",
    value: HOLLYWOOD_QUALIFICATION_OPTION_VALUES.investmentReady,
  },
  {
    id: HOLLYWOOD_QUALIFICATION_OPTION_IDS.investmentConsidering,
    label: "Do 500 zł miesięcznie - rozważam, jeśli będzie dopasowane do mnie",
    value: HOLLYWOOD_QUALIFICATION_OPTION_VALUES.investmentConsidering,
  },
  {
    id: HOLLYWOOD_QUALIFICATION_OPTION_IDS.investmentBelow200,
    label: "Poniżej 200 zł - szukam tańszych rozwiązań na ten moment",
    value: HOLLYWOOD_QUALIFICATION_OPTION_VALUES.investmentBelow200,
  },
] satisfies HollywoodQualificationOption[];

export const HOLLYWOOD_SPRING_QUALIFICATION_OPTIONS_V2 = [
  {
    id: HOLLYWOOD_QUALIFICATION_OPTION_IDS.investmentReady,
    label: "Chcę dokonać pełnej metamorfozy w Programie Hollywood i jestem gotowa na taką inwestycję",
    value: HOLLYWOOD_QUALIFICATION_OPTION_VALUES.investmentReady,
  },
  {
    id: HOLLYWOOD_QUALIFICATION_OPTION_IDS.investmentConsidering,
    label: "Jestem gotowa na inwestycję, ale chcę sprawdzić czy Program Hollywood jest dla mnie",
    value: HOLLYWOOD_QUALIFICATION_OPTION_VALUES.investmentConsidering,
  },
  {
    id: HOLLYWOOD_QUALIFICATION_OPTION_IDS.investmentBelow200,
    label: "Na ten moment to za dużo - wolę tańszą, samodzielną opcję (bez trenera)",
    value: HOLLYWOOD_QUALIFICATION_OPTION_VALUES.investmentBelow200,
  },
] satisfies HollywoodQualificationOption[];

/**
 * `budget` arm of the `metamorfoza2026-qualification-budget` experiment: the same three
 * buckets, but phrased as a monthly budget instead of a level of commitment. Ids and
 * values are deliberately unchanged, so the downsell trigger, the financing-offer popup
 * and every report keyed on `qual-*` keep working across both arms.
 */
export const HOLLYWOOD_BUDGET_QUALIFICATION_OPTIONS = [
  {
    id: HOLLYWOOD_QUALIFICATION_OPTION_IDS.investmentReady,
    label: "500-700 zł miesięcznie - jestem gotowa rozpocząć swoją metamorfozę!",
    value: HOLLYWOOD_QUALIFICATION_OPTION_VALUES.investmentReady,
  },
  {
    id: HOLLYWOOD_QUALIFICATION_OPTION_IDS.investmentConsidering,
    label: "Około 500 zł miesięcznie - potrzebuję więcej informacji, zanim podejmę decyzję",
    value: HOLLYWOOD_QUALIFICATION_OPTION_VALUES.investmentConsidering,
  },
  {
    id: HOLLYWOOD_QUALIFICATION_OPTION_IDS.investmentBelow200,
    label: "Poniżej 300 zł miesięcznie - w tym momencie nie kwalifikuję się do Programu Hollywood",
    value: HOLLYWOOD_QUALIFICATION_OPTION_VALUES.investmentBelow200,
  },
] satisfies HollywoodQualificationOption[];

export const HOLLYWOOD_QUALIFICATION_DOWNSELL_OPTION_IDS = {
  standard: [HOLLYWOOD_QUALIFICATION_OPTION_IDS.simplePlan],
  spring: [HOLLYWOOD_QUALIFICATION_OPTION_IDS.investmentBelow200],
} as const;

/**
 * What `/api/calendly/book` sends at position 4. The investment labels are the options of that
 * question in the Calendly event type (event_types/0192013f…), and the sales-call webhook matches
 * them to skip Meta QualifiedLead for a downsell answer, so an option renamed in Calendly is
 * renamed here (TRE-2380).
 */
export const HOLLYWOOD_QUALIFICATION_CALENDLY_ANSWER_LABELS_BY_VALUE = {
  [HOLLYWOOD_QUALIFICATION_OPTION_VALUES.investmentReady]:
    "Chcę dokonać pełnej metamorfozy w Programie Hollywood (kilkaset zł miesięcznie)",
  [HOLLYWOOD_QUALIFICATION_OPTION_VALUES.investmentConsidering]:
    "Jestem gotowa na inwestycję, ale chcę sprawdzić czy Program Hollywood jest dla mnie",
  [HOLLYWOOD_QUALIFICATION_OPTION_VALUES.investmentBelow200]:
    "Szukam krótkiego, samodzielnego planu do działania (bez trenera)",
  [HOLLYWOOD_QUALIFICATION_OPTION_VALUES.fullMetamorphosis]:
    "Chcę schudnąć raz na zawsze (metamorfoza ze wsparciem trenera)",
  [HOLLYWOOD_QUALIFICATION_OPTION_VALUES.simplePlan]:
    "Wolę działać sama według gotowego planu, bez regularnych konsultacji",
} as const;

/** The downsell option as Calendly listed it before TRE-1351, still on meetings booked back then. */
const HOLLYWOOD_LEGACY_DOWNSELL_CALENDLY_ANSWER_LABELS = [
  "Szukam krótkiego, samodzielnego planu do działania",
] as const;

export const HOLLYWOOD_DOWNSELL_QUALIFICATION_VALUES = [
  HOLLYWOOD_QUALIFICATION_OPTION_VALUES.simplePlan,
  HOLLYWOOD_QUALIFICATION_OPTION_VALUES.investmentBelow200,
] as const;

const HOLLYWOOD_DOWNSELL_QUALIFICATION_ANSWER_MATCHERS = [
  ...HOLLYWOOD_DOWNSELL_QUALIFICATION_VALUES,
  ...HOLLYWOOD_DOWNSELL_QUALIFICATION_VALUES.map(
    (value) => HOLLYWOOD_QUALIFICATION_CALENDLY_ANSWER_LABELS_BY_VALUE[value],
  ),
  ...HOLLYWOOD_LEGACY_DOWNSELL_CALENDLY_ANSWER_LABELS,
  ...HOLLYWOOD_QUALIFICATION_OPTIONS.filter((option) =>
    HOLLYWOOD_DOWNSELL_QUALIFICATION_VALUES.includes(
      option.value as (typeof HOLLYWOOD_DOWNSELL_QUALIFICATION_VALUES)[number],
    ),
  ).map((option) => option.label),
  ...HOLLYWOOD_SPRING_QUALIFICATION_OPTIONS.filter((option) =>
    HOLLYWOOD_DOWNSELL_QUALIFICATION_VALUES.includes(
      option.value as (typeof HOLLYWOOD_DOWNSELL_QUALIFICATION_VALUES)[number],
    ),
  ).map((option) => option.label),
  ...HOLLYWOOD_SPRING_QUALIFICATION_OPTIONS_V2.filter((option) =>
    HOLLYWOOD_DOWNSELL_QUALIFICATION_VALUES.includes(
      option.value as (typeof HOLLYWOOD_DOWNSELL_QUALIFICATION_VALUES)[number],
    ),
  ).map((option) => option.label),
  ...HOLLYWOOD_BUDGET_QUALIFICATION_OPTIONS.filter((option) =>
    HOLLYWOOD_DOWNSELL_QUALIFICATION_VALUES.includes(
      option.value as (typeof HOLLYWOOD_DOWNSELL_QUALIFICATION_VALUES)[number],
    ),
  ).map((option) => option.label),
] as const;

const normalizeAnswer = (value: string | null | undefined): string => {
  if (typeof value !== "string") {
    return "";
  }

  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
};

const HOLLYWOOD_DOWNSELL_QUALIFICATION_ANSWER_MATCHER_SET = new Set(
  HOLLYWOOD_DOWNSELL_QUALIFICATION_ANSWER_MATCHERS.map(normalizeAnswer),
);

export const isHollywoodDownsellQualificationAnswer = (answer: string | null | undefined): boolean => {
  if (!answer) {
    return false;
  }

  return HOLLYWOOD_DOWNSELL_QUALIFICATION_ANSWER_MATCHER_SET.has(normalizeAnswer(answer));
};
